import React, { useEffect } from 'react';
import {
  Form,
  FormInstance,
  DateSerialized,
  SelectBooleanInput
} from '@lightbeamhealth/blueprint-form';
import { Row, Col } from '../../../../grid';
import { StartDate } from '../date';
import { EndDate } from '../date';
import SelectMethod from '../method';
import SelectLanguage from '../language';
import SelectTime from '../time';
import Weekday from '../weekday';
import Timezone from '../timezone';
import { CardBlock, CardBreakout } from '../../card';
import Callout from '../../callout';
import {
  GetCurrentTZ,
  GetFormalDate,
  ShorthandDate
} from '../../../../libs/time';
import { useMetaData } from '../../../../contexts/metadata';

interface EpxAsthmaData {
  method: string;
  language: string;
  start: string;
  end?: string;
  tz: string;
  isFixed: boolean;
  times: string[];
  days: number[];
  step?: number;
  isScreeningRolling?: boolean;
  shouldAskFever?: boolean;
  shouldRecheckOnWorse?: boolean;
  shouldAlertOnWorseWithoutRescue?: boolean;
}

type EpxAsthmaDataRaw = DateSerialized<EpxAsthmaData, 'start' | 'end'>;

export const EpxAsthmaDefaultData: EpxAsthmaData = {
  method: 'message',
  language: 'en',
  start: GetFormalDate(ShorthandDate.Today),
  end: null,
  tz: GetCurrentTZ(),
  isFixed: true,
  times: ['10:00'],
  days: [1, 4],
  step: 1,
  isScreeningRolling: false,
  shouldAskFever: false,
  shouldRecheckOnWorse: false,
  shouldAlertOnWorseWithoutRescue: false
};

const DATE_FORMAT = 'YYYY-MM-DD';
const { DeserializeDate, SerializeDate } = FormInstance;

interface EpxAsthmaFormProps {
  setForm?: (form: FormInstance<EpxAsthmaDataRaw>) => void;
  onSubmit?: (data: EpxAsthmaData) => void;
  isDisabled?: boolean;
}

const EpxAsthmaForm = ({
  setForm,
  onSubmit,
  isDisabled
}: EpxAsthmaFormProps): JSX.Element => {
  const { terms } = useMetaData();
  const serialize = (data: EpxAsthmaDataRaw): EpxAsthmaData => {
    return {
      ...data,
      start: SerializeDate(data.start, DATE_FORMAT),
      end: SerializeDate(data.end, DATE_FORMAT)
    };
  };
  const deserialize = (data: EpxAsthmaData): EpxAsthmaDataRaw => {
    return {
      ...data,
      start: DeserializeDate(data.start, DATE_FORMAT),
      end: DeserializeDate(data.end, DATE_FORMAT)
    };
  };
  const defaultData = deserialize(EpxAsthmaDefaultData);
  const form = new FormInstance<EpxAsthmaDataRaw, EpxAsthmaData>(defaultData, {
    serialize,
    deserialize
  });
  useEffect(() => {
    if (setForm) {
      setForm(form);
    }
  }, []);
  return (
    <CardBlock bare>
      <Form form={form} onSubmit={onSubmit} disabled={isDisabled}>
        <CardBlock>
          <Row>
            <Col sm={12}>
              <SelectMethod />
            </Col>
            <Col sm={12}>
              <SelectLanguage />
            </Col>
          </Row>
        </CardBlock>
        <CardBlock>
          <Row>
            <Col sm={12}>
              <SelectBooleanInput
                label="Schedule Type"
                name="isFixed"
                trueLabel="Fixed Schedule"
                falseLabel="SMART Schedule"
                fill
              />
            </Col>
          </Row>
        </CardBlock>
        <CardBlock>
          <Row>
            <Col sm={12}>
              <StartDate label="Start Date" name="start" fill />
            </Col>
            <Col sm={12}>
              <EndDate label="End Date" name="end" />
            </Col>
            <Col sm={12}>
              <SelectTime label="Message At" name="times[0]" />
            </Col>
            <Col sm={12}>
              <Timezone label="Timezone" name="tz" fill />
            </Col>
          </Row>
        </CardBlock>
        <CardBreakout>
          <Row>
            <Col sm={24}>
              <Weekday name="days" minItems={1} inline abbreviated />
            </Col>
          </Row>
        </CardBreakout>
        <CardBlock>
          <Row>
            <Col sm={12}>
              <SelectBooleanInput
                label="Ask Fever"
                name="shouldAskFever"
                fill
              />
            </Col>
            <Col sm={12}>
              <SelectBooleanInput
                label="Should Recheck On Worse"
                name="shouldRecheckOnWorse"
                fill
              />
            </Col>
            <Col sm={12}>
              <SelectBooleanInput
                label="Alert On Worse Without Rescue Inhaler"
                name="shouldAlertOnWorseWithoutRescue"
                fill
              />
            </Col>
          </Row>
        </CardBlock>
        <CardBlock>
          <Col sm={24}>
            <SelectBooleanInput
              label="Turn on Alert-Based Screener"
              name="isScreeningRolling"
              fill
            />
            <Callout className="mt-4">
              Turning on this option will ask the {terms.patient.default} the
              Asthma screening question only if they have not triggered an
              Asthma program alert within the past 4 weeks.
            </Callout>
          </Col>
        </CardBlock>
      </Form>
    </CardBlock>
  );
};

export default EpxAsthmaForm;
